import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyPayUResponse, parsePayUResponse } from '../utils/payu';
import { getPayUConfig } from '../config/payu.config';
import './PaymentSuccess.css';

function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [verifying, setVerifying] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [verificationError, setVerificationError] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Parse URL parameters using utility function
        const response = parsePayUResponse(location.search);

        // Verify payment response hash
        const config = getPayUConfig();
        const isValid = await verifyPayUResponse(response, config.merchantSalt, config.merchantKey);

        if (isValid && response.status === 'success') {
          setPaymentDetails({
            transactionId: response.txnid,
            amount: response.amount,
            courseName: response.productinfo,
            paymentMode: response.mode,
            bankRefNumber: response.bank_ref_num,
            status: response.status
          });
        } else {
          setVerificationError('Payment verification failed. Please contact support.');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setVerificationError('Error verifying payment. Please contact support.');
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [location.search]);

  const handleGoHome = () => {
    navigate('/');
  };

  if (verifying) {
    return (
      <div className="payment-status-container">
        <div className="payment-status-card">
          <div className="spinner"></div>
          <h2>Verifying Payment...</h2>
          <p>Please wait while we verify your payment.</p>
        </div>
      </div>
    );
  }

  if (verificationError) {
    return (
      <div className="payment-status-container">
        <div className="payment-status-card error">
          <div className="status-icon error-icon">✗</div>
          <h2>Verification Failed</h2>
          <p>{verificationError}</p>
          <button className="status-button" onClick={handleGoHome}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-status-container">
      <div className="payment-status-card success">
        <div className="status-icon success-icon">✓</div>
        <h2>Payment Successful!</h2>
        <p className="success-message">
          Thank you for your enrollment. Your payment has been processed successfully.
        </p>
        <p className="confirmation-message">
          📧 You will receive a confirmation email within 24 hours with course access details.
        </p>

        {paymentDetails && (
          <div className="payment-details">
            <h3>Payment Details</h3>
            <div className="detail-row">
              <span className="detail-label">Transaction ID:</span>
              <span className="detail-value">{paymentDetails.transactionId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Course:</span>
              <span className="detail-value">{paymentDetails.courseName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Amount Paid:</span>
              <span className="detail-value">₹{paymentDetails.amount}</span>
            </div>
            {paymentDetails.paymentMode && (
              <div className="detail-row">
                <span className="detail-label">Payment Mode:</span>
                <span className="detail-value">{paymentDetails.paymentMode}</span>
              </div>
            )}
            {paymentDetails.bankRefNumber && (
              <div className="detail-row">
                <span className="detail-label">Bank Reference:</span>
                <span className="detail-value">{paymentDetails.bankRefNumber}</span>
              </div>
            )}
          </div>
        )}

        <div className="next-steps">
          <h3>Next Steps:</h3>
          <ol>
            <li>Check your email for course access details</li>
            <li>Complete the assigned projects</li>
            <li>Submit your work for review</li>
            <li>Receive your certificate upon completion</li>
          </ol>
        </div>

        <button className="status-button" onClick={handleGoHome}>
          Go to Home
        </button>
      </div>
    </div>
  );
}

export default PaymentSuccess;
